import { useSetChatroomStatusMutation } from '@pba/react-chat-core';
import { type ChatRoom, isGroupChatroom } from 'models';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from 'permissions';
import { Icon, type MenuItemType } from 'ui';
// TODO: Fix import once work on ChatGroupInfoModal and ChatUserInfoModal is completed
import { ChatGroupInfoModal } from '../../chat-header/chat-group-header/chat-group-info-modal';
import { ChatUserInfoModal } from '../../chat-header/chat-user-header/chat-user-info-modal';
import { useDeleteChatConfirmModal } from '../../hooks';

type P = {
  onChatDeleted: () => Promise<void>;
  selectedChatroomId?: string;
};

export const useGetChatroomSidebarActions = ({ onChatDeleted, selectedChatroomId }: P) => {
  const { t } = useTranslation();
  const [infoChatroom, setInfoChatroom] = useState<ChatRoom | null>(null);
  const { canDeleteChat } = usePermissions();
  const { mutate: setChatroomStatus } = useSetChatroomStatusMutation();

  const handleChatRemoved = useCallback(
    async (chatroomId: string) => {
      if (chatroomId === selectedChatroomId) {
        await onChatDeleted();
      }
    },
    [onChatDeleted, selectedChatroomId],
  );

  const { confirm: confirmDeleteChat, contextHolder } = useDeleteChatConfirmModal({
    onSuccess: handleChatRemoved,
  });

  const getActions = useCallback(
    // TODO: remove showMarkAsReadAndUnread in next verison and always allow mark as read and unread
    (chatroom: ChatRoom, showMarkAsReadAndUnread?: boolean): MenuItemType[] => {
      const actions: MenuItemType[] = [];

      if (showMarkAsReadAndUnread) {
        const isUnread = (chatroom.stats?.unread ?? 0) > 0;

        actions.push(
          isUnread
            ? {
                icon: <Icon icon="round-line" />,
                key: 'mark-as-read',
                label: t('Mark as read'),
                onClick({ domEvent }) {
                  domEvent.stopPropagation();
                  setChatroomStatus({ chatroomId: chatroom.id, status: 'read' });
                },
              }
            : {
                icon: <Icon icon="round-fill" />,
                key: 'mark-as-unread',
                label: 'Mark as unread',
                onClick({ domEvent }) {
                  domEvent.stopPropagation();
                  setChatroomStatus({ chatroomId: chatroom.id, status: 'unread' });
                },
              },
        );
      }

      actions.push({
        icon: <Icon icon="information-line" />,
        key: 'info',
        label: t('Info'),
        onClick({ domEvent }) {
          domEvent.stopPropagation();
          setInfoChatroom(chatroom);
        },
      });

      if (canDeleteChat({ chatroom })) {
        actions.push({
          danger: true,
          icon: <Icon icon="delete-2-line" />,
          key: 'delete',
          label: t('Delete chat'),
          onClick({ domEvent }) {
            domEvent.stopPropagation();
            confirmDeleteChat(chatroom.id);
          },
        });
      }

      return actions;
    },
    [canDeleteChat, confirmDeleteChat, setChatroomStatus, t],
  );

  const infoModal = useMemo(() => {
    if (!infoChatroom) {
      return null;
    }

    const closeInfoModal = () => {
      setInfoChatroom(null);
    };

    const handleInfoChatDeleted = async () => {
      closeInfoModal();
      await handleChatRemoved(infoChatroom.id);
    };

    if (isGroupChatroom({ chatroom: infoChatroom })) {
      return (
        <ChatGroupInfoModal
          chatroom={infoChatroom}
          isOpened
          onChatDeleted={handleInfoChatDeleted}
          onClose={closeInfoModal}
        />
      );
    }

    return (
      <ChatUserInfoModal
        chatroom={infoChatroom}
        isOpened
        onChatDeleted={handleInfoChatDeleted}
        onClose={closeInfoModal}
      />
    );
  }, [handleChatRemoved, infoChatroom]);

  return useMemo(
    () => ({
      contextHolder,
      getActions,
      infoModal,
    }),
    [contextHolder, getActions, infoModal],
  );
};
